import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import RelicStageOne from './RelicStageOne';
import RelicStageTwo from './RelicStageTwo';
import RelicStageThree from './RelicStageThree';
import { useIsMobile } from '@/hooks/use-mobile';
import BulletinMetaTags from '../../meta/BulletinMetaTags';

interface RelicInspectViewProps {
  bulletin: any;
  selectedId: string;
  onClose: () => void;
}

const RelicInspectView: React.FC<RelicInspectViewProps> = ({
  bulletin,
  selectedId,
  onClose
}) => {
  const isMobile = useIsMobile();
  const [currentStage, setCurrentStage] = useState<1 | 2 | 3>(1);
  const [hasUserLiked, setHasUserLiked] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Touch handling for swipe-to-dismiss
  const touchStartY = React.useRef<number | null>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // Check if user has already liked this relic to determine starting stage
  useEffect(() => {
    // This will be handled by BulletinLikeHandler component
    // For now, we'll start at stage 1 and let the like handler tell us the state
  }, [bulletin.id]);

  const handleLikeEngagement = () => {
    setHasUserLiked(true);
    setCurrentStage(2);
  };

  const handleInfoClick = () => {
    setCurrentStage(3);
  };

  const handleBackFromStage3 = () => {
    setCurrentStage(2);
  };

  // Handle touch events for swipe-to-dismiss
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobile && isLoaded) {
      touchStartY.current = e.touches[0].clientY;
      if (e.currentTarget === overlayRef.current) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isMobile && touchStartY.current !== null && isLoaded) {
      const touchEndY = e.changedTouches[0].clientY;
      const diffY = touchEndY - touchStartY.current;
      
      if (diffY > 100) {
        onClose();
      }
      
      touchStartY.current = null;
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current && isLoaded) {
      onClose();
    }
  };

  return (
    <>
      {/* Background overlay (match BulletinDetailView) */}
      <AnimatePresence>
        <motion.div
          ref={overlayRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 bg-black/60"
          style={{ 
            zIndex: 40,
            touchAction: 'none',
            pointerEvents: isLoaded ? 'auto' : 'none'
          }}
          transition={{ duration: 0.15 }}
        />
      </AnimatePresence>

      {/* Stage container */}
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{ 
          zIndex: 50,
          pointerEvents: 'none',
          padding: '0'
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <BulletinMetaTags bulletin={bulletin} />
        
        <div
          className="relative w-full h-full flex items-center justify-center"
          style={{ 
            pointerEvents: 'auto',
            zIndex: 60,
            maxWidth: '90vw',
            maxHeight: '90vh'
          }}
        >
          <AnimatePresence mode="wait">
            {currentStage === 1 && (
              <RelicStageOne
                key="stage-1"
                bulletin={bulletin}
                onClose={onClose}
                onLikeEngagement={handleLikeEngagement}
                hasUserLiked={hasUserLiked}
                setHasUserLiked={setHasUserLiked}
                setCurrentStage={setCurrentStage}
              />
            )}
            
            {currentStage === 2 && (
              <RelicStageTwo
                key="stage-2"
                bulletin={bulletin}
                onClose={onClose}
                onInfoClick={handleInfoClick}
              />
            )}
            
            {currentStage === 3 && (
              <RelicStageThree
                key="stage-3"
                bulletin={bulletin}
                onClose={onClose}
                onBack={handleBackFromStage3}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default RelicInspectView;